import React, { useState } from "react";
import axios from "axios";
import { FaPhoneAlt, FaChevronDown } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';

const validatePhone = (phone) => {
  const cleaned = phone.replace(/[\s.\-()+]/g, '');
  return /^(33|0)[0-9]{9}$/.test(cleaned);
};

const QuickCallWidget = ({ isDark }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [phone, setPhone] = useState("");
    const [phoneError, setPhoneError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handlePhoneChange = (e) => {
        const val = e.target.value.replace(/[^\d\s.\-+()]/g, '');
        setPhone(val);
        if (val && !validatePhone(val)) {
            setPhoneError("Format invalide (ex: 06 12 34 56 78)");
        } else {
            setPhoneError("");
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!phone.trim()) {
            setPhoneError("Veuillez entrer votre numéro");
            return;
        }
        if (!validatePhone(phone)) {
            setPhoneError("Format invalide (ex: 06 12 34 56 78)");
            return;
        }
        setIsSubmitting(true);
        const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
        const payload = {
            nom: "Client",
            prenom: "Rappel",
            email: "rappel@immediat.com",
            telephone: phone,
            message: "⚠️ DEMANDE DE RAPPEL URGENT",
            services: "Non précisé",
            type: "RAPPEL_RAPIDE"
        };

        try {
            await axios.post(`${API_URL}/api/contact`, payload);
            toast.success("Demande envoyée ! Un expert vous rappelle sous 15 minutes.", {
                position: "bottom-right",
                autoClose: 5000,
                theme: isDark ? "dark" : "light"
            });
            setPhone("");
            setPhoneError("");
            setIsOpen(false);
        } catch (error) {
            toast.error("Oups ! Une erreur est survenue.", {
                position: "bottom-right",
                theme: isDark ? "dark" : "light"
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-[9999] flex flex-col items-end">
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.9, originBottomRight: true }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.9 }}
                        className={`rounded-3xl shadow-2xl p-5 md:p-6 mb-4 w-[calc(100vw-2rem)] max-w-[320px] border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-blue-50'}`}
                    >
                        <div className="flex items-center gap-3 md:gap-4 mb-4">
                            <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl md:rounded-2xl bg-blue-50 flex items-center justify-center text-blue-600 shrink-0">
                                <FaPhoneAlt size={16} />
                            </div>
                            <div>
                                <h3 className={`font-bold text-base md:text-lg ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>Assistance rapide</h3>
                                <p className={`text-[10px] md:text-xs ${isDark ? 'text-green-400' : 'text-green-600'} font-semibold flex items-center gap-1 mt-1`}>
                                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                                    Conseillers disponibles
                                </p>
                            </div>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="mb-3">
                                <input
                                    type="tel"
                                    value={phone}
                                    onChange={handlePhoneChange}
                                    placeholder="Ex: 06 12 34 56 78"
                                    maxLength={20}
                                    className={`w-full rounded-xl px-4 py-3 outline-none border transition-colors text-sm ${
                                        phoneError
                                            ? 'border-red-400 bg-red-50'
                                            : isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-gray-50 border-gray-200 text-[#1B365D]'
                                    }`}
                                    disabled={isSubmitting}
                                />
                                {phoneError && (
                                    <p className="text-red-500 text-[11px] mt-1 px-1 font-medium">{phoneError}</p>
                                )}
                            </div>
                            <button
                                type="submit"
                                disabled={isSubmitting || !!phoneError}
                                className="w-full bg-[#1B365D] text-white font-bold py-3 rounded-xl hover:bg-blue-900 shadow-lg shadow-blue-900/30 transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSubmitting ? "Envoi..." : "Me rappeler"}
                            </button>
                        </form>
                    </motion.div>
                )}
            </AnimatePresence>

            <motion.button
                onClick={() => setIsOpen(!isOpen)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-[#1B365D] text-white flex items-center justify-center w-12 h-12 md:w-14 md:h-14 rounded-full md:rounded-2xl shadow-xl hover:bg-blue-900 transition-colors relative"
            >
                {!isOpen && (
                    <span className="absolute inset-0 rounded-full md:rounded-2xl bg-[#1B365D] animate-ping opacity-40"></span>
                )}
                <motion.div animate={{ rotate: isOpen ? 180 : 0 }}>
                    {isOpen ? <FaChevronDown size={18} /> : <FaPhoneAlt size={18} />}
                </motion.div>
            </motion.button>
        </div>
    );
};

export default QuickCallWidget;
